import { UserRoundIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useApiErrorMessage } from '@/api/errors'
import { assignLeads, type LeadBulkResult } from '@/api/leads'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useColleagues } from '@/hooks/useColleagues'
import { userLabel } from '@/lib/userDisplay'

/** The Select needs a value, and an empty string is not one Radix accepts. */
const UNASSIGN = 'UNASSIGN'

/**
 * What to do with the leads that are ticked.
 *
 * The Monday after a fair is a hundred leads and one decision, which is the whole reason this
 * exists: without it, "everything from Südback goes to Jonas" is a hundred trips through a row
 * menu. It goes to the backend as **one request** — a loop of single calls would be a hundred
 * round trips and a half-applied change whenever one of them failed.
 *
 * A lead somebody else owns is reported back as skipped rather than refusing the batch, so the
 * message here says what actually happened rather than claiming everything was changed.
 *
 * Handing leads on asks first: an assignment moves who is responsible for two dozen
 * conversations, and the wrong name picked from a list is not something the person who now owns
 * them will notice for a while.
 */
export function LeadSelectionBar({
  selected,
  onChanged,
  onClear,
}: {
  selected: string[]
  /** After a batch lands — the rows on screen are stale the moment it does. */
  onChanged: () => void
  onClear: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const [busy, setBusy] = useState(false)
  const colleagues = useColleagues(true)

  /** The pick waiting to be confirmed: a colleague's id, or null for taking the assignments back. */
  const [handingTo, setHandingTo] = useState<{ id: string | null } | null>(null)
  const recipient = colleagues.find((colleague) => colleague.id === handingTo?.id)

  async function run(action: () => Promise<LeadBulkResult>) {
    if (busy) {
      return
    }
    setBusy(true)
    try {
      const result = await action()
      toast.success(t('leadSelection.updated', { count: result.updated.length }))
      if (result.skipped.length > 0) {
        toast.warning(t('leadSelection.skipped', { count: result.skipped.length }))
      }
      onChanged()
      onClear()
    } catch (error) {
      toast.error(errorMessage(error, t('leadSelection.failed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      // Laid over the toolbar, at exactly its size (see `LeadsPage`): opaque, so nothing of the
      // toolbar shows through, and tinted inside so it reads as another mode of the same strip.
      className="bg-card absolute inset-0 border-b"
      role="region"
      // A stable label: a landmark that renames itself on every tick is one nobody can navigate
      // to. The count is announced by the live region below instead.
      aria-label={t('leadSelection.region')}
    >
      <div className="bg-muted/50 flex h-full flex-wrap content-center items-center gap-x-3 gap-y-2 px-4 py-2">
        <span className="text-sm font-medium" aria-live="polite">
          {t('leadSelection.count', { count: selected.length })}
        </span>

        <Select
          // Never holds a value: picking a name *is* the action, and a name left showing in the
          // trigger would read as the assignee of the selection.
          value=""
          disabled={busy || colleagues.length === 0}
          // Picking a name opens the confirmation rather than doing it: this is the half of the bar
          // that is hard to notice having got wrong.
          onValueChange={(id) => id && setHandingTo({ id: id === UNASSIGN ? null : id })}
        >
          <SelectTrigger size="sm" className="w-56" aria-label={t('leadSelection.assign')}>
            <UserRoundIcon className="size-3.5" aria-hidden />
            <SelectValue placeholder={t('leadSelection.assign')} />
          </SelectTrigger>
          <SelectContent>
            {colleagues.map((colleague) => (
              <SelectItem
                key={colleague.id}
                value={colleague.id}
                // Kept in the list so an existing assignment still has a name against it — it is
                // just not somebody to hand more work to.
                disabled={!colleague.assignable}
                // Says why it is greyed out: an entry with no reason on it is a dead end for
                // anybody who cannot see the styling.
                title={colleague.assignable ? undefined : t('leadSelection.assignable')}
              >
                {userLabel(colleague, colleague.id)}
              </SelectItem>
            ))}
            <SelectSeparator />
            <SelectItem value={UNASSIGN}>{t('leadSelection.unassign')}</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="ghost" size="sm" className="ml-auto" onClick={onClear} disabled={busy}>
          <XIcon />
          {t('leadSelection.clear')}
        </Button>

        <AlertDialog open={handingTo !== null} onOpenChange={(open) => !open && setHandingTo(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('leadSelection.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {handingTo?.id
                  ? t('leadSelection.confirmAssign', {
                      count: selected.length,
                      // The id is the last resort for an account with neither name nor address; it
                      // is still better than a sentence with a hole where the name should be.
                      name: recipient ? userLabel(recipient, handingTo.id) : handingTo.id,
                    })
                  : t('leadSelection.confirmUnassign', { count: selected.length })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                disabled={busy}
                onClick={() => {
                  const target = handingTo?.id ?? null
                  setHandingTo(null)
                  void run(() => assignLeads(selected, target))
                }}
              >
                {t('leadSelection.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
